import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, Loader } from 'lucide-react';

export default function UploadZone({ onFiles, loading, onClose }) {
  const onDrop = useCallback(accepted => {
    const csvFiles = accepted.filter(f => f.name.endsWith('.csv') || f.type === 'text/csv');
    if (csvFiles.length) onFiles(csvFiles);
  }, [onFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    multiple: true,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.85)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 100,
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Sheet slides up from bottom on mobile, centered on desktop */}
      <div style={{
        background: 'var(--bg2)', borderTop: '1px solid var(--border)',
        borderRadius: '10px 10px 0 0',
        padding: '20px 20px 32px',
        width: '100%', maxWidth: 480,
        boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
      }}>
        {/* Handle bar */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border2)', margin: '0 auto 18px' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 600 }}>Import CSV Files</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', padding: 4, display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        <div
          {...getRootProps()}
          style={{
            border: `1px dashed ${isDragActive ? 'var(--accent)' : 'var(--border2)'}`,
            borderRadius: 6, padding: '28px 20px',
            textAlign: 'center', cursor: loading ? 'not-allowed' : 'pointer',
            background: isDragActive ? 'rgba(91,184,245,0.05)' : 'var(--bg3)',
            transition: 'all 0.15s',
          }}
        >
          <input {...getInputProps()} disabled={loading} />
          {loading ? (
            <>
              <Loader size={24} style={{ color: 'var(--accent)', margin: '0 auto 10px', animation: 'spin 1s linear infinite', display: 'block' }} />
              <p style={{ color: 'var(--text2)', fontSize: 13 }}>Processing CSV…</p>
            </>
          ) : (
            <>
              <Upload size={24} style={{ color: 'var(--text3)', margin: '0 auto 10px', display: 'block' }} />
              <p style={{ color: 'var(--text)', fontSize: 13, marginBottom: 4 }}>
                {isDragActive ? 'Drop CSV files here' : 'Tap to choose a file'}
              </p>
              <p style={{ color: 'var(--text3)', fontSize: 12 }}>or drag & drop on desktop</p>
            </>
          )}
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <FileText size={12} style={{ color: 'var(--text3)', marginTop: 2, flexShrink: 0 }} />
          <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
            Data is yours. We don't save anything. All processing happens locally in your browser.
          </p>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
